(() => {
  "use strict";

  const PREFIX = "[FTR Loop Discount Fix]";
  const LOOP_BUNDLE_API =
    "https://api.loopsubscriptions.com/storefront/2023-10/bundle";

  function positiveNumber(value) {
    if (value == null || value === "") return null;
    const number = Number(String(value).split("/").pop());
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function normalizeId(value) {
    if (value == null || value === "") return null;
    const text = String(value).trim();
    return text || null;
  }

  function unwrap(payload) {
    if (!payload || payload.success === false) return payload?.data ?? payload;
    return payload.data ?? payload;
  }

  function extractTransactionId(payload) {
    const data = unwrap(payload) || {};
    const candidates = [
      data.transactionId,
      data.bundleTransactionId,
      data.loopBundleGuid,
      data.txnId,
      data.id,
      data.transaction?.transactionId,
      data.transaction?.bundleTransactionId,
      payload?.transactionId,
      payload?.bundleTransactionId,
      payload?.loopBundleGuid,
      payload?.txnId,
      payload?.id,
    ];

    for (const candidate of candidates) {
      const value = normalizeId(candidate);
      if (value && value.length >= 16) return value;
    }

    return null;
  }

  function getPublicBundleId(state) {
    return normalizeId(
      state.publicBundleId ||
        state.bundleDetails?.nanoId ||
        state.bundleDetails?.id ||
        state.bundleDetails?.data?.nanoId ||
        state.bundleDetails?.data?.id,
    );
  }

  function getSelectedSellingPlanShopifyId(state, finalItems) {
    return positiveNumber(
      state.selectedSellingPlanShopifyId ||
        state.getSelectedSellingPlanId?.() ||
        finalItems.find((item) => item?.selling_plan)?.selling_plan,
    );
  }

  function getSelectedQuantity(finalItems) {
    return finalItems.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0,
    );
  }

  function getBoxSizes(details) {
    details = details?.data || details || {};
    if (Array.isArray(details.boxSizes)) return details.boxSizes;
    if (Array.isArray(details.variants)) return details.variants;
    return [];
  }

  function matchesQuantity(box, quantity) {
    const exact = Number(box.quantity ?? box.size ?? box.itemCount);
    if (Number.isFinite(exact) && exact > 0) return exact === quantity;

    const min = Number(
      box.minimum ?? box.min ?? box.minQuantity ?? box.minSize ?? box.minValue,
    );
    const max = Number(
      box.maximum ?? box.max ?? box.maxQuantity ?? box.maxSize ?? box.maxValue,
    );

    return (
      (!Number.isFinite(min) || min <= 0 || quantity >= min) &&
      (!Number.isFinite(max) || max <= 0 || quantity <= max)
    );
  }

  function getPublicBoxSizeId(state, productInfo, quantity) {
    const selectedFromDom = productInfo?.querySelector?.(
      "[data-loop-box-size-id][aria-selected='true'], [data-loop-box-size-id].selected",
    )?.dataset.loopBoxSizeId;

    if (selectedFromDom) return String(selectedFromDom);

    const candidates = [
      ...getBoxSizes(state.bundleDetails),
      ...getBoxSizes(state.hostedBundleDetails),
    ];

    const matching = candidates.filter((box) => matchesQuantity(box, quantity));
    const box = matching.find((entry) => entry.nanoId) || matching[0] || null;

    return normalizeId(
      box?.nanoId ||
        box?.boxSizeId ||
        box?.publicId ||
        box?.id ||
        state.selectedBoxSizeId,
    );
  }

  function purchaseTypeLabel(discount) {
    return String(
      discount?.purchaseType ||
        discount?.purchase_type ||
        discount?.appliesToPurchaseType ||
        discount?.purchaseTypeName ||
        "",
    )
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "_");
  }

  function isSubscriptionDiscount(discount) {
    const type = purchaseTypeLabel(discount);
    return type.includes("SUBSCRIPTION") || type.includes("SUBSCRIBE");
  }

  function isOneTimeDiscount(discount) {
    const type = purchaseTypeLabel(discount);
    return (
      type.includes("ONE_TIME") ||
      type.includes("ONETIME") ||
      type.includes("ONE_TIME_PURCHASE") ||
      type === "ONE"
    );
  }

  function discountId(discount) {
    return normalizeId(
      discount?.nanoId ||
        discount?.discountId ||
        discount?.bundleDiscountId ||
        discount?.id,
    );
  }

  function looksLikeRealDiscount(value) {
    if (!value || typeof value !== "object" || !discountId(value)) return false;

    const typeLabel = purchaseTypeLabel(value);
    const name = String(value.__typename || value.name || value.title || "");

    return Boolean(
      value.bundleDiscountId != null ||
      value.discountId != null ||
      value.discountValue != null ||
      value.discountPercentage != null ||
      value.percentage != null ||
      value.oneTimeDiscount != null ||
      value.sellingPlanComputedDiscounts != null ||
      typeLabel ||
      /discount/i.test(name),
    );
  }

  function collectDiscounts(value, output = [], seen = new Set(), depth = 0) {
    if (!value || depth > 7) return output;

    if (Array.isArray(value)) {
      value.forEach((entry) =>
        collectDiscounts(entry, output, seen, depth + 1),
      );
      return output;
    }

    if (typeof value !== "object" || seen.has(value)) return output;
    seen.add(value);

    if (looksLikeRealDiscount(value)) output.push(value);

    Object.values(value).forEach((entry) => {
      if (entry && typeof entry === "object") {
        collectDiscounts(entry, output, seen, depth + 1);
      }
    });

    return output;
  }

  function getSelectedHostedVariant(state, quantity) {
    const hosted =
      state.hostedBundleDetails?.data || state.hostedBundleDetails || {};
    const variants = Array.isArray(hosted.variants) ? hosted.variants : [];

    const selectedBoxSizeId = normalizeId(state.selectedBoxSizeId);
    const exact = variants.find((variant) =>
      [
        variant?.nanoId,
        variant?.id,
        variant?.bundleVariantId,
        variant?.boxSizeId,
      ].some(
        (value) =>
          value != null &&
          selectedBoxSizeId &&
          String(value) === selectedBoxSizeId,
      ),
    );

    if (exact) return exact;
    return (
      variants.find((variant) => matchesQuantity(variant, quantity)) || null
    );
  }

  function getBundleDiscountId(state, productInfo, quantity) {
    const selectedFromDom = productInfo?.querySelector?.(
      "[data-loop-discount-id][aria-selected='true'], [data-loop-discount-id].selected",
    )?.dataset.loopDiscountId;

    const isSubscription = state.purchaseType === "subscription";
    const selected = state.selectedBundleDiscount;

    if (isSubscription) {
      if (selectedFromDom) return String(selectedFromDom);

      if (
        selected &&
        discountId(selected) &&
        isSubscriptionDiscount(selected)
      ) {
        return discountId(selected);
      }

      const allSubscriptionDiscounts = [
        ...collectDiscounts(state.bundleDetails),
        ...collectDiscounts(state.hostedBundleDetails),
      ];

      const matchingSubscription = allSubscriptionDiscounts.find(
        isSubscriptionDiscount,
      );
      return matchingSubscription ? discountId(matchingSubscription) : null;
    }

    // One-time bundles must use the one-time discount associated with the
    // currently selected Loop box/variant. Never inherit a subscription discount.
    const hostedVariant = getSelectedHostedVariant(state, quantity);
    const variantDiscounts = [
      ...(Array.isArray(hostedVariant?.discounts)
        ? hostedVariant.discounts
        : []),
      ...(hostedVariant?.discount ? [hostedVariant.discount] : []),
    ].filter(looksLikeRealDiscount);

    const explicitVariantOneTime = variantDiscounts.find(isOneTimeDiscount);
    if (explicitVariantOneTime) return discountId(explicitVariantOneTime);

    const all = [
      ...collectDiscounts(state.hostedBundleDetails),
      ...collectDiscounts(state.bundleDetails),
    ];

    const explicitOneTime = all.find(isOneTimeDiscount);
    if (explicitOneTime) return discountId(explicitOneTime);

    // Older Loop responses sometimes omit purchaseType. In that case, use only
    // a discount-shaped object belonging to the matched variant before looking
    // globally. Do not treat arbitrary response objects as discounts.
    const genericVariantDiscount = variantDiscounts.find(
      (discount) =>
        !purchaseTypeLabel(discount) && !isSubscriptionDiscount(discount),
    );
    if (genericVariantDiscount) return discountId(genericVariantDiscount);

    const generic = all.find(
      (discount) =>
        !purchaseTypeLabel(discount) && !isSubscriptionDiscount(discount),
    );
    if (generic) return discountId(generic);

    if (selectedFromDom) return String(selectedFromDom);

    return null;
  }

  function buildProductVariants(finalItems) {
    const quantities = new Map();

    finalItems.forEach((item) => {
      const id = positiveNumber(item.id || item.variant_id || item.variantId);
      const quantity = Number(item.quantity || 0);
      if (!id || !Number.isFinite(quantity) || quantity <= 0) return;
      quantities.set(id, (quantities.get(id) || 0) + quantity);
    });

    return Array.from(quantities, ([shopifyId, quantity]) => ({
      shopifyId,
      quantity,
    }));
  }

  function getBundleName(state) {
    const details = state.bundleDetails?.data || state.bundleDetails || {};
    const hosted =
      state.hostedBundleDetails?.data || state.hostedBundleDetails || {};
    return (
      normalizeId(
        details.bundleName ||
          details.name ||
          details.title ||
          hosted.bundleName ||
          hosted.name ||
          hosted.title,
      ) || "Bundle"
    );
  }

  async function createOfficialTransaction(state, finalItems, productInfo) {
    const bundleId = getPublicBundleId(state);
    const sellingPlanShopifyId = getSelectedSellingPlanShopifyId(
      state,
      finalItems,
    );
    const productVariants = buildProductVariants(finalItems);
    const quantity = getSelectedQuantity(finalItems);
    const boxSizeId = getPublicBoxSizeId(state, productInfo, quantity);
    const discountId = getBundleDiscountId(state, productInfo, quantity);

    if (!bundleId) throw new Error("Loop public bundle ID is missing.");
    if (state.purchaseType === "subscription" && !sellingPlanShopifyId)
      throw new Error("Shopify selling plan ID is missing.");
    if (!productVariants.length)
      throw new Error("Loop bundle product variants are missing.");
    if (!boxSizeId) throw new Error("Loop public box size ID is missing.");

    const payload = {
      boxSizeId,
      discountId,
      sellingPlanShopifyId:
        state.purchaseType === "subscription" ? sellingPlanShopifyId : null,
      productVariants,
      quantity: 1,
    };

    console.log(PREFIX, "Official transaction request:", {
      bundleId,
      payload,
      selectedBundleDiscount: state.selectedBundleDiscount,
    });

    const transactionUrl = `${LOOP_BUNDLE_API}/${encodeURIComponent(bundleId)}/transaction`;

    async function sendTransaction(attempt, requestPayload = payload) {
      const response = await fetch(transactionUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestPayload),
      });

      const rawText = await response.text();
      let responsePayload = null;

      try {
        responsePayload = rawText ? JSON.parse(rawText) : null;
      } catch (_) {
        responsePayload = rawText;
      }

      console.log(PREFIX, "Official transaction response:", {
        attempt,
        status: response.status,
        requestPayload,
        payload: responsePayload,
      });

      return { response, responsePayload, requestPayload };
    }

    let { response, responsePayload, requestPayload } =
      await sendTransaction(1);
    let usedDiscountFallback = false;

    // Loop currently returns a generic 500 for some one-time bundle transactions
    // when an explicit discountId is supplied. One-time transactions themselves
    // are valid with sellingPlanShopifyId: null, so retry once without discountId.
    // The discount metadata is then carried to Shopify via cart attributes below.
    if (
      state.purchaseType !== "subscription" &&
      payload.discountId &&
      (!response.ok || responsePayload?.success === false) &&
      response.status >= 500
    ) {
      const fallbackPayload = { ...payload };
      delete fallbackPayload.discountId;
      usedDiscountFallback = true;
      console.warn(
        PREFIX,
        "One-time transaction failed with discountId; retrying without discountId.",
        {
          discountId: payload.discountId,
          fallbackPayload,
        },
      );
      await new Promise((resolve) => setTimeout(resolve, 250));
      ({ response, responsePayload, requestPayload } = await sendTransaction(
        2,
        fallbackPayload,
      ));
    } else if (
      (!response.ok || responsePayload?.success === false) &&
      response.status >= 500
    ) {
      await new Promise((resolve) => setTimeout(resolve, 450));
      ({ response, responsePayload, requestPayload } =
        await sendTransaction(2));
    }

    if (!response.ok || responsePayload?.success === false) {
      const message =
        responsePayload?.message ||
        responsePayload?.code ||
        `Loop transaction failed (${response.status}).`;
      throw new Error(message);
    }

    const transactionId = extractTransactionId(responsePayload);
    if (!transactionId) {
      throw new Error("Loop transaction did not return a transaction ID.");
    }

    return {
      transactionId,
      payload,
      requestPayload,
      responsePayload,
      usedDiscountFallback,
    };
  }

  function getOneTimeDiscountMetadata(state) {
    const discount = state.selectedBundleDiscount;
    if (!discount || typeof discount !== "object") return null;

    const type =
      discount.type || discount.discountType || discount.valueType || null;
    const value =
      discount.value ??
      discount.discountValue ??
      discount.percentage ??
      discount.discountPercentage ??
      null;
    const computed =
      discount.oneTimeDiscount ??
      discount.computedDiscount ??
      discount.discountComputedValue ??
      null;

    if (type == null && value == null && computed == null) return null;
    return {
      discountType: type,
      discountValue: value,
      discountComputedValue: computed,
    };
  }

  async function prepareLoopDiscountCartAttributes(transactionId, state) {
    const metadata = getOneTimeDiscountMetadata(state);
    if (!metadata || !transactionId) return null;

    let existing = {};
    try {
      const cartResponse = await fetch(
        `${window.Shopify?.routes?.root || "/"}cart.js`,
        {
          headers: { Accept: "application/json" },
        },
      );
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const raw = cart?.attributes?._loopBundleDiscountAttributes;
        if (raw) {
          try {
            existing = JSON.parse(raw) || {};
          } catch (_) {}
        }
      }
    } catch (_) {}

    return {
      _loopBundleDiscountAttributes: JSON.stringify({
        ...existing,
        [transactionId]: metadata,
      }),
    };
  }

  function replaceTransactionId(items, transactionId) {
    return items.map((item) => {
      if (!item?.properties?._bundleId) return item;
      return {
        ...item,
        properties: {
          ...item.properties,
          _bundleId: transactionId,
        },
      };
    });
  }

  function install() {
    const state = window.FTRLoopBundle;
    if (!state?.prepareCartItems) {
      setTimeout(install, 50);
      return;
    }

    if (state.prepareCartItems.__ftrDiscountFixed) return;

    const originalPrepare = state.prepareCartItems.bind(state);

    const wrappedPrepare = async function (items, productInfo) {
      // One-time custom bundles bypass the legacy/internal transaction and Cart Transform
      // completely. Create the official Loop transaction directly from selected children.
      if (state.purchaseType !== "subscription") {
        const cleanItems = (Array.isArray(items) ? items : [])
          .map((item) => ({
            id: positiveNumber(item?.id || item?.variant_id || item?.variantId),
            quantity: Number(item?.quantity || 0),
          }))
          .filter(
            (item) =>
              item.id && Number.isFinite(item.quantity) && item.quantity > 0,
          );

        if (!cleanItems.length)
          throw new Error("No valid one-time bundle products were selected.");

        const official = await createOfficialTransaction(
          state,
          cleanItems,
          productInfo || document,
        );
        const bundleName = getBundleName(state);

        state.pendingBundleCartAttributes = official.usedDiscountFallback
          ? await prepareLoopDiscountCartAttributes(
              official.transactionId,
              state,
            )
          : null;
        const grouped = cleanItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          properties: {
            _bundleId: official.transactionId,
            _bundleName: bundleName,
          },
        }));

        try {
          sessionStorage.setItem(
            `ftr-loop-bundle-name:${official.transactionId}`,
            bundleName,
          );
        } catch (_) {}

        console.log(
          PREFIX,
          "Prepared one-time Loop child-only cart items:",
          grouped,
        );
        return grouped;
      }

      state.pendingBundleCartAttributes = null;
      const prepared = await originalPrepare(items, productInfo);

      if (
        !Array.isArray(prepared) ||
        !prepared.some((item) => item?.properties?._bundleId)
      ) {
        return prepared;
      }

      const official = await createOfficialTransaction(
        state,
        prepared,
        productInfo || document,
      );
      const corrected = replaceTransactionId(prepared, official.transactionId);

      try {
        const oldTransactionId = prepared.find(
          (item) => item?.properties?._bundleId,
        )?.properties?._bundleId;
        const bundleName = corrected.find(
          (item) => item?.properties?._bundleName,
        )?.properties?._bundleName;

        if (bundleName) {
          sessionStorage.setItem(
            `ftr-loop-bundle-name:${official.transactionId}`,
            bundleName,
          );
        }

        if (oldTransactionId) {
          const oldUrl = sessionStorage.getItem(
            `ftr-loop-bundle-url:${oldTransactionId}`,
          );
          if (oldUrl)
            sessionStorage.setItem(
              `ftr-loop-bundle-url:${official.transactionId}`,
              oldUrl,
            );
        }
      } catch (_) {}

      console.log(PREFIX, "Corrected Loop bundle cart items:", corrected);
      return corrected;
    };

    wrappedPrepare.__ftrDiscountFixed = true;
    state.prepareCartItems = wrappedPrepare;

    console.log(
      PREFIX,
      "Official Loop bundle transaction + discount patch enabled.",
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", install, { once: true });
  } else {
    install();
  }
})();
